import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ProfileSettings } from '@/components/settings/ProfileSettings';
import { SecuritySettings } from '@/components/settings/SecuritySettings';
import { BillingSettings } from '@/components/settings/BillingSettings';
import { KeySettings } from '@/components/settings/KeySettings';
import { User, Shield, CreditCard, Key, Settings as SettingsIcon } from 'lucide-react';
import type { PageProps } from '@/types/page';

const tabs = [
    { id: 'profile', label: 'Profile', icon: User, description: 'Manage your personal information' },
    { id: 'security', label: 'Security', icon: Shield, description: 'Password and authentication' },
    { id: 'billing', label: 'Billing & Team', icon: CreditCard, description: 'Subscription and team management' },
    { id: 'keys', label: 'AI Keys', icon: Key, description: 'API keys and integrations' },
] as const;

type TabId = typeof tabs[number]['id'];

export default function SettingsPage({ nav = true, footer = false }: PageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const initialTab = useMemo<TabId>(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab') as TabId | null;
        const valid = tabs.some(t => t.id === tab);
        return (valid ? tab : 'profile') as TabId;
    }, [location.search]);
    const [activeTab, setActiveTab] = useState<TabId>(initialTab);

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'profile':
                return <ProfileSettings />;
            case 'security':
                return <SecuritySettings />;
            case 'billing':
                return <BillingSettings />;
            case 'keys':
                return <KeySettings />;
            default:
                return <ProfileSettings />;
        }
    };

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
                <div className="mx-auto max-w-7xl px-4 py-12">
                    {/* Header Section */}
                    <div className="mb-12 text-center lg:text-left">
                        <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                            <div className="p-3 bg-primary/10 rounded-xl">
                                <SettingsIcon className="size-8 text-primary" />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    Settings
                                </h1>
                                <p className="text-muted-foreground mt-1 text-lg">
                                    Manage your account settings and preferences
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col xl:flex-row gap-8">
                        {/* Tab Navigation */}
                        <div className="xl:w-80 flex-shrink-0">
                            <Card className="p-2">
                                <CardContent className="p-4">
                                    <nav className="space-y-2">
                                        {tabs.map((tab) => {
                                            const Icon = tab.icon;
                                            const isActive = activeTab === tab.id;
                                            return (
                                                <Button
                                                    key={tab.id}
                                                    variant={isActive ? 'default' : 'ghost'}
                                                    className={`w-full justify-start h-auto p-4 transition-all duration-200 ${isActive
                                                            ? 'shadow-md bg-primary text-primary-foreground hover:bg-primary/90'
                                                            : 'hover:bg-accent/50 hover:shadow-sm'
                                                        }`}
                                                    onClick={() => {
                                                        setActiveTab(tab.id);
                                                        const params = new URLSearchParams(location.search);
                                                        params.set('tab', tab.id);
                                                        navigate({ pathname: location.pathname, search: params.toString() });
                                                    }}
                                                >
                                                    <div className="flex items-start gap-3 w-full">
                                                        <Icon className={`size-5 mt-0.5 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                                                        <div className="text-left flex-1">
                                                            <div className="font-medium">{tab.label}</div>
                                                            <div className={`text-xs mt-1 ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                                                                {tab.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Button>
                                            );
                                        })}
                                    </nav>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 min-w-0">
                            <Card className="min-h-[600px]">
                                <CardContent className="p-8">
                                    <div className="animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
                                        {renderTabContent()}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}
